import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import {
  type PaginatedData,
  paginatedListResponseToPaginatedData,
} from "@/nzds/data-grid"
import type {
  BillPeriod,
  BillPeriodListApiQueryOptions,
  BillPeriodListResponse,
} from "@/services/core-api/types"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import type { UseInfiniteQueryOptions } from "@tanstack/react-query"
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"

import { getBillPeriods } from "./get-bill-periods"

interface BillPeriodsPaginatedData
  extends BillPeriodListResponse, PaginatedData<BillPeriod> {}

interface UseBillPeriodsInfiniteOptions {
  apiQueryOptions: Omit<BillPeriodListApiQueryOptions, "offset">
  organizationId: Organization["id"]
  queryOptions?: Omit<
    UseInfiniteQueryOptions<BillPeriodsPaginatedData, HttpError>,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam" | "select"
  >
}

/** Pages bill periods one screen at a time, the way `useBillsInfinite` pages bills. */
export const useBillPeriodsInfinite = ({
  apiQueryOptions,
  organizationId,
  queryOptions = {},
}: UseBillPeriodsInfiniteOptions) => {
  const { placeholderData, ...restQueryOptions } = queryOptions

  const queryKey = useQueryKey(ApiQueryName.BillPeriods, "getMany", null, {
    apiQueryOptions,
    infinite: true,
    organizationId,
  })

  const queryResult = useInfiniteQuery<BillPeriodsPaginatedData, HttpError>({
    ...restQueryOptions,
    enabled: Boolean(organizationId) && (restQueryOptions.enabled ?? true),
    placeholderData: placeholderData ?? keepPreviousData,
    queryKey,
    queryFn: async ({ pageParam }) => {
      const response = await getBillPeriods({
        apiQueryOptions: {
          ...apiQueryOptions,
          offset: Number(pageParam),
        },
        organizationId,
      })

      return paginatedListResponseToPaginatedData(response)
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.items.length
      if (nextOffset >= lastPage.total) {
        return undefined
      }
      return nextOffset
    },
    throwOnError: restQueryOptions.throwOnError ?? false,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.BillPeriods)
}
